// Import necessary components and hooks
import { Card, Form, Select, Flex, InputNumber, Slider, Row, Col, Switch, Button, Image, Tooltip, Typography } from "antd";
import { themeState } from "../lib/atom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import React, { useEffect, useMemo, useState } from 'react';
import { canvasNoticeState, deletePanelIndex, panelsOK, polygonDetailsSelector, selectedIndicesState, solarPanel, polygonsState } from "../ThreeJs/ReactStates/states";
import { sceneManager } from '../ThreeJs/SetUps/SceneManager';
import { autoPopulateSegment } from '../ThreeJs/Panels/AutoPopulate';
import { DrawingModes, POLYGONS } from "../ThreeJs/Global/variables";
import { DeleteOutlined, DownOutlined } from "@ant-design/icons";
import {
	removeExtraPanels,
	removeGrid,
	updateSelectedPanelsConfig,
	updateSelectedPanelsTransform,
} from "../ThreeJs/Panels/PanelFunctions";
import InfoModal from "./InfoModal";
import { addPanelSelector } from "../ThreeJs/ReactStates/updates";
import { indexToLabel } from "../ThreeJs/Segments/Labels";
import { toast } from "../lib/toast";
import { getPanelImage } from "../helpers/panelImageCache";

// Neutral inline placeholder shown when a panel thumbnail is missing or fails
// to load (e.g. network/CORS) — avoids the browser's broken-image icon.
const PANEL_IMAGE_PLACEHOLDER =
	"data:image/svg+xml;utf8," +
	encodeURIComponent(
		"<svg xmlns='http://www.w3.org/2000/svg' width='30' height='30'>" +
			"<rect width='30' height='30' rx='4' fill='#1b3b44'/>" +
			"<path d='M7 20l5-6 3 3 4-5 4 8z' fill='#3a5560'/>" +
			"<circle cx='11' cy='10' r='2.2' fill='#3a5560'/>" +
			"</svg>"
	);

// Main component for managing solar panel settings
const SolarPanelSettings = ({ updateDrawingMode, currentDrawingMode, setShowPanelSettings, deleteConfirmation, panelList, compact = false }) => {
	const theme = useRecoilState(themeState)[0]; // Get current theme (dark/light)
	const textStrong = "var(--ui-text-strong)";
	const textMuted = "var(--ui-text-muted)";
	const details = useRecoilValue(polygonDetailsSelector); // Polygon metadata like azimuth and pitch
	const [modalOpen, setModalOpen] = useState(false); // State for info modal visibility
	const [isLoading, setIsLoading] = React.useState(false);
	const [panel, setSolarPanel] = useRecoilState(solarPanel); // Currently selected panel data
	const [arePanelsOK, setPanelsOK] = useRecoilState(panelsOK);
	const selectedIndices = useRecoilValue(selectedIndicesState); // Selected panel indices
	const polygons = useRecoilValue(polygonsState);
	const setPolygons = useSetRecoilState(polygonsState);
	const setCanvasNotice = useSetRecoilState(canvasNoticeState);
	const setDeletePanels = useSetRecoilState(deletePanelIndex); // Recoil setter for panels to delete
	const addPanel = useSetRecoilState(addPanelSelector);
	const { panelIndex } = selectedIndices; // Extract selected panel indices
	const hasSelectedPanels = Array.isArray(panelIndex) && panelIndex.length > 0;
	const [isPlacingPanels, setIsPlacingPanels] = useState(false);
	const [showTiltSlider, setShowTiltSlider] = useState(false);
	const [showRotationSlider, setShowRotationSlider] = useState(false);
	const [form] = Form.useForm();
	const panelSelectionKey = Array.isArray(panelIndex) ? panelIndex.join(",") : "none";

	const safePanelList = useMemo(() => (Array.isArray(panelList) ? panelList : []), [panelList]);

	// Generate Select dropdown options from panel list
	const options = safePanelList.map(panel => {
		const img = getPanelImage(panel.id); // Retrieve stored image
		return {
			value: panel.id,
			label: (
				<Flex gap='small' style={{ alignItems: 'center' }}>
					{/* Prefer the cached thumbnail; fall back to the remote URL, and
					    finally to a neutral placeholder so a failed/CORS image never
					    shows a broken-image icon. */}
					<Image
						src={img || panel.image}
						fallback={PANEL_IMAGE_PLACEHOLDER}
						preview={false}
						width={30}
						id={panel.id.toString()}
					/>
					{panel.name}
				</Flex>
			),
			
			searchText: [panel.name, panel.manufacturer, panel.model]
				.filter(Boolean)
				.join(" ")
				.toLowerCase()
		}
	});

	// Match every whitespace-separated term, so "lg 335" finds "LG335N1C-V5".
	const filterModuleOption = (input, option) => {
		const terms = input.trim().toLowerCase().split(/\s+/).filter(Boolean);
		if (terms.length === 0) return true;
		const haystack = option?.searchText || "";
		return terms.every(term => haystack.includes(term));
	};

	// Default values for the form
	const initialVals = useMemo(() => ({
		id: panel.id ?? safePanelList[0]?.id ?? null,
		azimuth: panel.azimuth ?? details?.azimu ?? 0,
		tilt: panel.tilt ?? 0,
		rotation: panel.rotation ?? 0,
		height: panel.height ?? 3,
		racking: panel.racking ?? "Flush",
		orientation: panel.orientation ?? "Portrait",
		moduleSpacingCol: panel.moduleSpacingCol ?? 1,
		moduleSpacingRow: panel.moduleSpacingRow ?? 1,
		arraySpacingCol: panel.arraySpacingCol ?? 0,
		arraySpacingRow: panel.arraySpacingRow ?? 0,
		arrayLayoutCol: panel.arrayLayoutCol ?? 1,
		arrayLayoutRow: panel.arrayLayoutRow ?? 1
	}), [panel, safePanelList, details?.azimu]);

	const selectedPanelValues = useMemo(() => {
		if (!hasSelectedPanels) return [];
		const segmentIdx = selectedIndices.segmentIndex;
		if (segmentIdx == null) return [];

		return panelIndex
			.map((idx) => {
				const uiPanel = polygons?.[segmentIdx]?.panels?.[idx] || {};
				const polyPanel = POLYGONS?.[segmentIdx]?.panels?.[idx] || {};
				const transform = polyPanel?.panel?.userData?.panelTransform || {};
				return {
					id: uiPanel?.id ?? polyPanel?.id ?? null,
					orientation: uiPanel?.orientation ?? panel?.orientation ?? "Portrait",
					tilt: uiPanel?.tilt ?? transform.tilt ?? panel?.tilt ?? 0,
					rotation: uiPanel?.rotation ?? transform.rotation ?? panel?.rotation ?? 0,
					azimuth: uiPanel?.azimuth ?? transform.azimuth ?? panel?.azimuth ?? 0,
					height: uiPanel?.height ?? panel?.height ?? 3,
				};
			})
			.filter(Boolean);
	}, [hasSelectedPanels, selectedIndices.segmentIndex, panelIndex, polygons, panel]);

	const getCommonValue = (values = []) => {
		const normalized = values
			.filter((val) => val !== null && val !== undefined && val !== "")
			.map((val) => String(val));
		const unique = [...new Set(normalized)];
		if (unique.length !== 1) return null;
		const first = values.find((val) => String(val) === unique[0]);
		return first ?? null;
	};

	const derivedFormValues = useMemo(() => {
		if (!hasSelectedPanels || selectedPanelValues.length === 0) {
			return { ...initialVals };
		}

		const moduleId = getCommonValue(selectedPanelValues.map((item) => item.id));
		const orientation = getCommonValue(selectedPanelValues.map((item) => item.orientation));
		const tilt = getCommonValue(selectedPanelValues.map((item) => item.tilt));
		const rotation = getCommonValue(selectedPanelValues.map((item) => item.rotation));
		const azimuth = getCommonValue(selectedPanelValues.map((item) => item.azimuth));
		const height = getCommonValue(selectedPanelValues.map((item) => item.height));

		return {
			...initialVals,
			id: moduleId ?? undefined,
			orientation: orientation ?? (panel?.orientation ?? "Portrait"),
			tilt: tilt ?? initialVals.tilt,
			rotation: rotation ?? initialVals.rotation,
			azimuth: azimuth ?? initialVals.azimuth,
			height: height ?? initialVals.height,
		};
	}, [hasSelectedPanels, selectedPanelValues, initialVals, panel?.orientation]);

	// Initialize panel state with form values once on mount
	useEffect(() => {
		if (safePanelList.length === 0) return;
		if (panel?.id == null) {
			setSolarPanel(initialVals);
		}
	}, [safePanelList.length, panel?.id, setSolarPanel, initialVals]);

	useEffect(() => {
		form.setFieldsValue(derivedFormValues);
	}, [
		form,
		selectedIndices.segmentIndex,
		panelSelectionKey,
		panel?.panel_id,
		derivedFormValues,
	]);

	useEffect(() => {
		setShowTiltSlider(false);
		setShowRotationSlider(false);
	}, [panelSelectionKey, selectedIndices.segmentIndex]);

	// Handle form field changes
	const handleChange = (changedValues, allValues) => {
		const updatedFields = { ...allValues };
		const changedTilt = Object.prototype.hasOwnProperty.call(changedValues, "tilt");
		const changedRotation = Object.prototype.hasOwnProperty.call(changedValues, "rotation");
		const changedModule = Object.prototype.hasOwnProperty.call(changedValues, "id");
		const changedOrientation = Object.prototype.hasOwnProperty.call(changedValues, "orientation");

		if (hasSelectedPanels && (changedTilt || changedRotation)) {
			const transformedPanel = updateSelectedPanelsTransform(
				selectedIndices.segmentIndex,
				panelIndex,
				{
					tilt: allValues.tilt,
					rotation: allValues.rotation,
				},
				polygons,
				setPolygons,
				setPanelsOK,
				arePanelsOK
			);

			if (transformedPanel) {
				updatedFields.tilt = transformedPanel.tilt;
				updatedFields.rotation = transformedPanel.rotation;
				updatedFields.azimuth = transformedPanel.azimuth;
				form.setFieldsValue({
					tilt: transformedPanel.tilt,
					rotation: transformedPanel.rotation,
					azimuth: transformedPanel.azimuth,
				});
			}
		}

		if (hasSelectedPanels && (changedModule || changedOrientation)) {
			const configUpdates = {};
			if (changedModule) configUpdates.id = allValues.id;
			if (changedOrientation) configUpdates.orientation = allValues.orientation;
			configUpdates.moduleSpacingCol = allValues.moduleSpacingCol;
			configUpdates.moduleSpacingRow = allValues.moduleSpacingRow;

			const configuredPanel = updateSelectedPanelsConfig(
				selectedIndices.segmentIndex,
				panelIndex,
				configUpdates,
				polygons,
				setPolygons,
				safePanelList,
				setPanelsOK,
				arePanelsOK
			);

			if (configuredPanel) {
				updatedFields.id = configuredPanel.id;
				updatedFields.orientation = configuredPanel.orientation;
				form.setFieldsValue({
					id: configuredPanel.id,
					orientation: configuredPanel.orientation,
				});
			}
		}

		setSolarPanel(prevState => { return { ...prevState, ...updatedFields } });
		updateDrawingMode(DrawingModes.NONE);  // Stop any current drawing mode
		setIsPlacingPanels(false);
	}

	// Activate drawing mode for manually adding new panels
	const handleAdd = () => {
		updateDrawingMode(DrawingModes.PANEL);
		setIsPlacingPanels(true);
		const pulseButton =
			document.getElementById("pulse-button") ||
			document.getElementById("pulse-button-dark");
		if (pulseButton) {
			pulseButton.style.animation = "none";
		}
	}

	const handlePlaceManually = () => {
		if (typeof setShowPanelSettings === "function") {
			setShowPanelSettings(true);
		}
		handleAdd();
	}

	// Mark panels for deletion
	const handleDelete = () => {
		setDeletePanels(panelIndex);
		deleteConfirmation(true); // Trigger confirmation modal
	}

	const handleAutoPopulate = async () => {
		setIsPlacingPanels(false);

		const LOADER_KEY = 'autopopulate';
		try {
			setIsLoading(true);
			toast.loading('Auto-populating panels…', { key: LOADER_KEY });

			// Call the auto-populate function with default options
			const uiPanelsSnapshot =
				polygons?.[selectedIndices.segmentIndex]?.panels ?? [];
			const result = await autoPopulateSegment(
				selectedIndices.segmentIndex,
				panel,
				addPanel,
				safePanelList,
				sceneManager.scene,
				sceneManager.camera,
				setPanelsOK,
				arePanelsOK,
				{},
				{ setPolygons, uiPanelsSnapshot }
			);

			if (result.panelsPlaced > 0) {
				// Reuse LOADER_KEY so the spinner toast morphs into the result.
				toast.success(`Successfully placed ${result.panelsPlaced} panels`, {
					key: LOADER_KEY,
					description: `Placed ${result.panelsPlaced} out of ${result.totalPositions} possible positions`,
					duration: 4000,
				});
			} else {
				toast.dismiss(LOADER_KEY);
			}
		} catch (error) {
			console.error('Error during auto-populate:', error);
			toast.dismiss(LOADER_KEY);
		} finally {
			setIsLoading(false);
		}
	};

	// Label element with info icon and tooltip
	const label = (
		<Typography.Text className="text-light">
			Choose Module
			{/* {panel.id !== null && (
				<Tooltip title="Module Information">
					<InfoCircleOutlined
						className="info"
						onClick={() => setModalOpen(true)}
					/>
				</Tooltip>
			)} */}
		</Typography.Text>
	)

	const darkIcon = (
		<DownOutlined
			style={{ color: textMuted }}
		/>
	)

	const watchedTilt = Form.useWatch("tilt", form);
	const watchedRotation = Form.useWatch("rotation", form);
	const watchedAzimuth = Form.useWatch("azimuth", form);
	const watchedHeight = Form.useWatch("height", form);
	const watchedOrientation = Form.useWatch("orientation", form);
	const displayAzimuth =
		watchedAzimuth ?? derivedFormValues?.azimuth ?? panel?.azimuth ?? details?.azimu ?? 0;
	const displayHeight =
		watchedHeight ?? derivedFormValues?.height ?? panel?.height ?? 3;
	const displayTilt = watchedTilt ?? derivedFormValues?.tilt ?? panel?.tilt ?? 0;
	const displayRotation =
		watchedRotation ?? derivedFormValues?.rotation ?? panel?.rotation ?? 0;

	const formatValue = (value, decimals = 2) => {
		const numeric = Number(value);
		if (!Number.isFinite(numeric)) return "-";
		if (Math.abs(numeric % 1) < 1e-6) return `${numeric}`;
		return numeric.toFixed(decimals);
	};

	const handleOrientationToggle = (checked) => {
		const orientation = checked ? "Landscape" : "Portrait";
		const currentValues = form.getFieldsValue(true);
		const nextValues = { ...currentValues, orientation };
		form.setFieldsValue({ orientation });
		handleChange({ orientation }, nextValues);
	};

	const segmentIndex = selectedIndices.segmentIndex;
	const segmentData = segmentIndex !== null && segmentIndex !== undefined
		? (POLYGONS[segmentIndex] || polygons[segmentIndex])
		: null;
	const segmentUi = segmentIndex !== null && segmentIndex !== undefined
		? polygons[segmentIndex]
		: null;

	const segmentLabelValue =
		segmentIndex !== null && segmentIndex !== undefined
			? (typeof segmentUi?.label === "string" && segmentUi.label) ||
			  (typeof segmentData?.label === "string" && segmentData.label) ||
			  indexToLabel(segmentIndex)
			: "";

	useEffect(() => {
		const shouldShowNotice = isPlacingPanels && segmentLabelValue !== "";

		if (shouldShowNotice) {
			setCanvasNotice({
				visible: true,
				intent: "info",
				source: "panel-placement",
				cancelable: true,
				title: `Placing panels for section ${segmentLabelValue}`,
				subtitle: "Press Esc to cancel.",
			});
			return;
		}

		setCanvasNotice((prev) => {
			if (prev?.source !== "panel-placement") return prev;
			if (!prev.visible) return prev;
			return { ...prev, visible: false, title: "", subtitle: "", source: null };
		});
	}, [isPlacingPanels, segmentLabelValue, setCanvasNotice]);

	useEffect(() => {
		if (!isPlacingPanels) return;

		const handleKeyDown = (event) => {
			if (event.key !== "Escape") return;
			event.preventDefault();
			updateDrawingMode(DrawingModes.NONE);
			setIsPlacingPanels(false);
			removeGrid();
			removeExtraPanels(polygons);
			setCanvasNotice((prev) => {
				if (prev?.source !== "panel-placement") return prev;
				if (!prev.visible) return prev;
				return { ...prev, visible: false, title: "", subtitle: "", source: null };
			});
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [isPlacingPanels, updateDrawingMode, polygons, setCanvasNotice]);

	useEffect(() => {
		if (currentDrawingMode === DrawingModes.PANEL) return;
		if (!isPlacingPanels) return;

		setIsPlacingPanels(false);
		removeGrid();
		removeExtraPanels(polygons);
		setCanvasNotice((prev) => {
			if (prev?.source !== "panel-placement") return prev;
			if (!prev.visible) return prev;
			return { ...prev, visible: false, title: "", subtitle: "", source: null };
		});
	}, [currentDrawingMode, isPlacingPanels, polygons, setCanvasNotice]);

	return (
		<div>
			{/* Main settings card */}
			<Card
				style={{ fontSize: '16px', width: '100%', position: 'relative' }}
				bodyStyle={{ padding: 0, fontSize: '16px' }}
				className={`segment__card solar-panel-settings-card ${compact ? "is-compact" : ""}`}
			>
				<Tooltip title='Delete selected module(s)'>
					<Button
						danger
						type="text"
						style={{ position: "absolute", top: 6, right: 8, zIndex: 3 }}
						disabled={!hasSelectedPanels}
						icon={<DeleteOutlined />}
						onClick={handleDelete}
						className={`${theme ? 'dark' : 'light'}`}
					/>
				</Tooltip>
				<div className="solar-panel-settings-title text-light" />
				<Form
					form={form}
					layout="vertical"
					style={{ padding: '10px 10px' }}
					className={` ${theme ? 'custom-scrollbar-dark' : 'custom-scrollbar'}`}
					initialValues={initialVals}
					onValuesChange={handleChange}
				>
					<Row justify='space-between' gutter={[16, 10]} align='middle'>
						{/* Module selection dropdown */}
						<Col span={24}>
							<Form.Item label={label} name="id">
									<Select
										suffixIcon={theme ? darkIcon : <DownOutlined />}
										options={options}
										placeholder="Search or select module"
										showSearch
										filterOption={filterModuleOption}
										notFoundContent="No matching modules"
										optionLabelProp="label"
										placement="bottomRight"
										size="large"
										variant={theme ? 'outlined' : 'filled'}
										dropdownStyle={theme ? { backgroundColor: 'black', color: textStrong } : {}}
										style={{ height: 'auto', alignItems: 'center' }}
									/>
								</Form.Item>
							</Col>
						<Form.Item name="orientation" style={{ display: "none" }}>
							<Select options={[{ value: "Portrait" }, { value: "Landscape" }]} />
						</Form.Item>
						<Form.Item name="azimuth" style={{ display: "none" }}>
							<InputNumber />
						</Form.Item>
						<Form.Item name="height" style={{ display: "none" }}>
							<InputNumber />
						</Form.Item>
						{compact ? (
							<Col span={24} className="solar-panel-compact-actions">
								<Button
									block
									type="primary"
									className="segment-add-button"
									onClick={handlePlaceManually}
									disabled={panel.id === null}
								>
									Place Manually
								</Button>
							</Col>
						) : (
							<>
								<Col span={24}>
									<Typography.Text className="solar-panel-settings-section text-light">
										Panel Settings
									</Typography.Text>
								</Col>
								<Col span={24}>
									<div className="solar-panel-settings-box">
										<Row justify="space-between" gutter={[16, 10]} align="middle">
								<Col span={12}>
									<Flex justify="space-between" align="center">
										<Typography.Text className="text-light">Azimuth(&deg;)</Typography.Text>
										<Typography.Text className="text-light">{formatValue(displayAzimuth)}</Typography.Text>
									</Flex>
								</Col>
								<Col span={12}>
									<Flex justify="space-between" align="center">
										<Typography.Text className="text-light">Height(in)</Typography.Text>
										<Typography.Text className="text-light">{formatValue(displayHeight)}</Typography.Text>
									</Flex>
								</Col>
								<Col span={24}>
									<Flex justify="space-between" align="center">
										<Typography.Text className="text-light">Tilt(&deg;)</Typography.Text>
										<InputNumber
											value={displayTilt}
											controls={false}
											readOnly
											style={{ width: 90 }}
											onClick={() => setShowTiltSlider((prev) => !prev)}
											onFocus={() => setShowTiltSlider(true)}
										/>
									</Flex>
									<div
										className="solar-panel-slider-wrap"
										style={{
											overflow: "hidden",
											maxHeight: showTiltSlider ? 64 : 0,
											opacity: showTiltSlider ? 1 : 0,
											transition: "max-height 0.25s ease, opacity 0.2s ease, margin-top 0.25s ease",
											marginTop: showTiltSlider ? 8 : 0,
										}}
									>
										<Form.Item name="tilt" style={{ marginBottom: 0 }}>
											<Slider min={0} max={90} step={1} />
										</Form.Item>
									</div>
								</Col>
								<Col span={24}>
									<Flex justify="space-between" align="center">
										<Typography.Text className="text-light">Rotation(&deg;)</Typography.Text>
										<InputNumber
											value={displayRotation}
											controls={false}
											readOnly
											style={{ width: 90 }}
											onClick={() => setShowRotationSlider((prev) => !prev)}
											onFocus={() => setShowRotationSlider(true)}
										/>
									</Flex>
									<div
										className="solar-panel-slider-wrap"
										style={{
											overflow: "hidden",
											maxHeight: showRotationSlider ? 64 : 0,
											opacity: showRotationSlider ? 1 : 0,
											transition: "max-height 0.25s ease, opacity 0.2s ease, margin-top 0.25s ease",
											marginTop: showRotationSlider ? 8 : 0,
										}}
									>
										<Form.Item name="rotation" style={{ marginBottom: 0 }}>
											<Slider min={-180} max={180} step={1} />
										</Form.Item>
									</div>
								</Col>
								{/* <Col span={12}>
									<Form.Item label="Racking" flex={2} name="racking">
										<Select
											suffixIcon={theme ? darkIcon : <DownOutlined />}
											options={[{ value: "Flush" }, { value: "Tilted", disabled: true }, { value: "Double Tilt", disabled: true }, { value: "Ground", disabled: true }]}
											dropdownStyle={theme && { backgroundColor: 'black', color: textStrong }}
										/>
									</Form.Item>
								</Col> */}
								<Col span={24}>
									<Flex justify="space-between" align="center">
										<Typography.Text className="text-light">Orientation</Typography.Text>
										<Switch
											checked={watchedOrientation === "Landscape"}
											onChange={handleOrientationToggle}
											checkedChildren="Landscape"
											unCheckedChildren="Portrait"
										/>
									</Flex>
								</Col>
										</Row>
									</div>
								</Col>
								{/* Commented out spacing/layout fields (for use with auto population functionality) */}
								{/* Module/array spacing and layout fields */}
								{/* <Col span={12}><p style={{ marginTop: '11px' }}>Module Spacing (in)</p></Col>
								<Col span={6}>
									<Form.Item label="Col" name="moduleSpacingCol">
										<InputNumber min={0} disabled />
									</Form.Item>
								</Col>
								<Col span={6}>
									<Form.Item label="Row" name="moduleSpacingRow">
										<InputNumber min={0} disabled />
									</Form.Item>
								</Col>
								<Col span={12}>Array Spacing (in)</Col>
								<Col span={6}>
									<Form.Item name="arraySpacingCol">
										<InputNumber min={0} disabled />
									</Form.Item>
								</Col>
								<Col span={6}>
									<Form.Item name="arraySpacingRow">
										<InputNumber min={0} disabled />
									</Form.Item>
								</Col>
								<Col span={12}>Array Layout (modules)</Col>
								<Col span={6}>
									<Form.Item name="arrayLayoutCol">
										<InputNumber min={0} disabled />
									</Form.Item>
								</Col>
								<Col span={6}>
									<Form.Item name="arrayLayoutRow">
										<InputNumber min={0} disabled />
									</Form.Item>
								</Col> */}
								<Col span={24} className="solar-panel-compact-actions">
									<Button
										block
										type="primary"
										className="segment-add-button"
										onClick={handlePlaceManually}
										disabled={panel.id === null}
										id={`${theme ? 'pulse-button-dark' : 'pulse-button'}`}
									>
										Place Manually
									</Button>
								</Col>
							</>
						)}
					</Row>
				</Form>
				<div className="solar-panel-settings-actions">
					<Button
						block
						type="primary"
						className="segment-add-button"
						onClick={handleAutoPopulate}
						loading={isLoading}
						// disabled
					>
						Auto Fill Modules
					</Button>
				</div>
			</Card>
			{/* Show info modal if a panel is selected */}
			{panel.id !== null && <InfoModal
				open={modalOpen}
				setOpen={setModalOpen}
				panelInfo={panel}
				panelList={safePanelList}
			/>}
		</div>
	)
}

export default SolarPanelSettings;
